import { useMemo, type ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { TimelineEvento } from "@/types/timeline";
import { TimelineEventDot } from "./TimelineEventDot";
import {
  formatEventoQuando,
  INFERRED_HINT,
  laneConfig,
  linkedinTransicaoDetalhe,
} from "./timelineEventUtils";

/** Segmento contínuo (lane de ESTADO), já com classe de cor e tooltip prontos. */
export interface LaneSegmento {
  fromMs: number;
  /** null = segue até o fim da janela (hoje). */
  toMs: number | null;
  className: string;
  tooltip: ReactNode;
}

interface TimelineLaneProps {
  label: string;
  windowFromMs: number;
  windowToMs: number;
  nowPercent: number;
  /** Lane de estado: barras contínuas (molde DisponibilidadeTimeline). */
  segments?: LaneSegmento[];
  /** Lane de evento: marcos discretos clicáveis. */
  eventos?: TimelineEvento[];
  /** Chave p/ ligar marcos com linha fina (ex.: evento => evento.vaga_id). */
  connectBy?: (evento: TimelineEvento) => string | null;
  onEventoClick?: (evento: TimelineEvento) => void;
  laneKey?: string;
}

const CLASSE_HACHURA_SEM_INFO =
  "bg-[repeating-linear-gradient(45deg,transparent,transparent_3px,#e2e8f0_3px,#e2e8f0_5px)]";

function clampPercent(percent: number) {
  return Math.min(Math.max(percent, 0), 100);
}

/**
 * Lane generalizada da linha do tempo: renderiza segmentos (estado) OU marcos
 * (eventos discretos) sobre o mesmo trilho posicionado por %, com marcador
 * "hoje". Reusa os primitivos visuais da DisponibilidadeTimeline. Compartilhada
 * entre a timeline do freelancer (components/freelancers/timeline) e a
 * timeline da agência (components/agencias/timeline) — mesmo shape de evento
 * (TimelineEvento), mesmo trilho.
 */
export function TimelineLane({
  label,
  windowFromMs,
  windowToMs,
  nowPercent,
  segments,
  eventos,
  connectBy,
  onEventoClick,
  laneKey,
}: TimelineLaneProps) {
  const totalMs = Math.max(windowToMs - windowFromMs, 1);
  const positionPercent = (ms: number) => clampPercent(((ms - windowFromMs) / totalMs) * 100);

  // Linhas finas ligando marcos do mesmo grupo (ex.: mesma vaga = mini-funil).
  const conectores = useMemo(() => {
    if (!eventos?.length || !connectBy) return [];
    const grupos = new Map<string, number[]>();
    for (const evento of eventos) {
      const chave = connectBy(evento);
      if (!chave) continue;
      const occurredMs = new Date(evento.occurred_at).getTime();
      if (occurredMs < windowFromMs || occurredMs > windowToMs) continue;
      const posicoes = grupos.get(chave) ?? [];
      posicoes.push(positionPercent(occurredMs));
      grupos.set(chave, posicoes);
    }
    const linhas: { leftPercent: number; widthPercent: number }[] = [];
    for (const posicoes of grupos.values()) {
      if (posicoes.length < 2) continue;
      const inicio = Math.min(...posicoes);
      const fim = Math.max(...posicoes);
      linhas.push({ leftPercent: inicio, widthPercent: Math.max(fim - inicio, 0.2) });
    }
    return linhas;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventos, connectBy, windowFromMs, windowToMs]);

  const laneVisual = laneConfig(laneKey ?? "");

  return (
    <div className="flex items-center gap-2">
      <span className="w-[104px] shrink-0 text-[11px] text-muted-foreground text-right">
        {label}
      </span>
      <div className="relative h-6 flex-1 rounded bg-slate-50">
        {/* Segmentos (estado) */}
        {segments?.map((segmento, indice) => {
          const inicioMs = Math.max(segmento.fromMs, windowFromMs);
          const fimMs = Math.min(segmento.toMs ?? windowToMs, windowToMs);
          if (fimMs <= windowFromMs || inicioMs >= windowToMs) return null;
          const leftPercent = positionPercent(inicioMs);
          const widthPercent = Math.max(positionPercent(fimMs) - leftPercent, 0.4);
          return (
            <Tooltip key={indice}>
              <TooltipTrigger asChild>
                <div
                  className={cn("absolute top-0.5 h-5 rounded-[2px]", segmento.className)}
                  style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
                />
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px]">
                {segmento.tooltip}
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Linhas finas ligando marcos da mesma vaga/projeto */}
        {conectores.map((linha, indice) => (
          <div
            key={`conector-${indice}`}
            className={cn("absolute top-1/2 h-px -translate-y-1/2", laneVisual.connectorClass)}
            style={{ left: `${linha.leftPercent}%`, width: `${linha.widthPercent}%` }}
            aria-hidden
          />
        ))}

        {/* Marcos (eventos discretos) */}
        {eventos?.map((evento) => {
          const occurredMs = new Date(evento.occurred_at).getTime();
          if (occurredMs < windowFromMs || occurredMs > windowToMs) return null;
          const leftPercent = positionPercent(occurredMs);
          return (
            <Tooltip key={evento.id}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => onEventoClick?.(evento)}
                  className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full focus:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
                  style={{ left: `${leftPercent}%` }}
                  aria-label={`${evento.label} em ${formatEventoQuando(evento.occurred_at)}`}
                >
                  <TimelineEventDot evento={evento} size="xs" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[260px]">
                <div className="space-y-0.5 text-xs">
                  <div className="font-medium">{evento.label}</div>
                  {linkedinTransicaoDetalhe(evento) && (
                    <div className="text-muted-foreground">{linkedinTransicaoDetalhe(evento)}</div>
                  )}
                  {evento.freelancer_nome && (
                    <div className="text-muted-foreground">{evento.freelancer_nome}</div>
                  )}
                  {evento.vaga_titulo && (
                    <div className="text-muted-foreground">{evento.vaga_titulo}</div>
                  )}
                  {evento.campanha_nome && (
                    <div className="text-muted-foreground">Campanha {evento.campanha_nome}</div>
                  )}
                  <div className="text-muted-foreground">
                    {formatEventoQuando(evento.occurred_at)}
                  </div>
                  {evento.inferred && <div className="text-amber-600">{INFERRED_HINT}</div>}
                  <div className="text-muted-foreground">Clique para ver detalhes</div>
                </div>
              </TooltipContent>
            </Tooltip>
          );
        })}

        {/* Marcador "hoje" */}
        <div
          className="absolute top-0 h-full w-px bg-foreground/40"
          style={{ left: `${clampPercent(nowPercent)}%` }}
          aria-hidden
        />
      </div>
    </div>
  );
}

export { CLASSE_HACHURA_SEM_INFO };
export default TimelineLane;
